import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip,
  Select, MenuItem, TextField, InputAdornment, Typography, Alert, Tabs, Tab
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import { loadThreats, setFilter, setAlertStatus, selectFilteredThreats } from './threatsSlice';
import { loadDetectionEngineering } from './detectionEngineeringSlice';
import DetectionEngineeringPanel from './DetectionEngineeringPanel';
import { severityChipSx, severityLabel, formatClock } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const STATUS_COLOR = {
  blocked: tokens.severity.low,
  flagged: tokens.severity.medium,
  investigating: tokens.severity.info
};
const ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'FALSE_POSITIVE'];

export default function ThreatsPage() {
  const dispatch = useDispatch();
  const filters = useSelector((s) => s.threats.filters);
  const events = useSelector(selectFilteredThreats);
  const allEvents = useSelector((s) => s.threats.events);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('alerts');

  useEffect(() => { if (tab === 'alerts') dispatch(loadThreats()); }, [dispatch, tab]);
  useEffect(() => { if (tab === 'engineering') dispatch(loadDetectionEngineering()); }, [dispatch, tab]);

  const handleStatusChange = async (id, status) => {
    setError(null);
    const result = await dispatch(setAlertStatus({ id, status }));
    if (setAlertStatus.rejected.match(result)) setError(result.error?.message || 'Could not update this alert.');
  };

  const types = ['all', ...new Set(allEvents.map((e) => e.type))];

  return (
    <Box>
      <PageHeader
        title="Threat Detection & Monitoring"
        subtitle="Real-time detection across logins, injection attempts, and anomalous traffic"
        action={<ModuleControlPanel basePath="/threats" moduleLabel="Threat Detection" />}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab value="alerts" label="Live Alerts" sx={{ minHeight: 36 }} />
        <Tab value="engineering" label="Detection Engineering" sx={{ minHeight: 36 }} />
      </Tabs>

      {tab === 'engineering' ? (
        <DetectionEngineeringPanel />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search IP, target, or type…"
          value={filters.search}
          onChange={(e) => dispatch(setFilter({ search: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 280 }}
        />
        <Select
          size="small" value={filters.severity}
          onChange={(e) => dispatch(setFilter({ severity: e.target.value }))}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All severities</MenuItem>
          {['critical', 'high', 'medium', 'low'].map((s) => (
            <MenuItem key={s} value={s}>{severityLabel(s)}</MenuItem>
          ))}
        </Select>
        <Select
          size="small" value={filters.type}
          onChange={(e) => dispatch(setFilter({ type: e.target.value }))}
          sx={{ minWidth: 190 }}
        >
          {types.map((t) => <MenuItem key={t} value={t}>{t === 'all' ? 'All attack types' : t}</MenuItem>)}
        </Select>
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Time', 'Severity', 'Type', 'Source IP', 'Origin', 'Target', 'Status'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {events.slice(0, 50).map((evt) => (
              <TableRow key={evt.id} hover>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', color: tokens.text.muted }}>
                  {formatClock(evt.timestamp)}
                </TableCell>
                <TableCell>
                  <Chip label={severityLabel(evt.severity)} size="small" sx={severityChipSx(evt.severity)} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: tokens.text.primary }}>{evt.type}</TableCell>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem' }}>{evt.sourceIp}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: tokens.text.secondary }}>{evt.country}</TableCell>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem', color: tokens.text.secondary }}>{evt.target}</TableCell>
                <TableCell>
                  <Select
                    size="small" variant="standard" value={evt.rawStatus || 'OPEN'}
                    onChange={(e) => handleStatusChange(evt.id, e.target.value)}
                    sx={{ fontSize: '0.75rem', color: STATUS_COLOR[evt.status] || tokens.text.secondary }}
                  >
                    {ALERT_STATUSES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s.replaceAll('_', ' ')}</MenuItem>)}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {events.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: tokens.text.muted }}>
              No events match these filters.
            </Typography>
          </Box>
        )}
      </Paper>
        </>
      )}
    </Box>
  );
}
